import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { OfferableResponse, Pokemon, Trade, TradeSideDto, TradesResponse } from '../lib/types';
import { Button, EmptyState, ErrorState, Modal, Panel, Tabs } from '../components/ui';
import { formatNumber, timeAgo } from '../lib/format';

function expiresIn(iso: string): string {
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  if (seconds <= 0) return 'expired';
  const hours = Math.floor(seconds / 3600);
  if (hours >= 24) return `${Math.floor(hours / 24)}d left`;
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.floor(seconds / 60))}m left`;
}

export function TradesPage() {
  const [params, setParams] = useSearchParams();
  const { push } = useToast();
  const { refresh, setWallet } = useSession();
  const state = useAsync<TradesResponse>(() => api.get('/trades'));
  const [tab, setTab] = useState<'incoming' | 'outgoing' | 'history'>('incoming');
  const [composing, setComposing] = useState<string | null>(params.get('with'));
  const [busy, setBusy] = useState<string | null>(null);

  // Arriving from a trainer's profile with ?with=Username opens the composer.
  useEffect(() => {
    const target = params.get('with');
    if (target) {
      setComposing(target);
      params.delete('with');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function act(trade: Trade, action: 'accept' | 'decline' | 'cancel') {
    setBusy(trade.id);
    try {
      const result = await api.post<{ wallet?: { pokeCoins: number; friendshipTokens: number } }>(
        `/trades/${trade.id}/${action}`,
      );
      if (action === 'accept') {
        setWallet(result.wallet ?? null);
        push('reward', `Trade with ${trade.partner.username} complete!`, '⇄');
      } else {
        push('info', action === 'decline' ? 'Offer declined.' : 'Offer withdrawn.');
      }
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
      state.reload();
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const data = state.data;
  const lists = { incoming: data?.incoming ?? [], outgoing: data?.outgoing ?? [], history: data?.history ?? [] };
  const shown = lists[tab];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Trades</h1>
          <div className="page-head__sub">
            Offers can't be edited once sent — cancel and re-send if you change your mind.
          </div>
        </div>
        <Button variant="primary" onClick={() => setComposing('')}>
          ⇄ New offer
        </Button>
      </div>

      <Panel raised={false}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'incoming', label: 'Incoming', badge: lists.incoming.length },
            { value: 'outgoing', label: 'Sent', badge: lists.outgoing.length },
            { value: 'history', label: 'History' },
          ]}
        />
      </Panel>

      {state.loading ? (
        <div className="pxwell skeleton" style={{ height: 220 }} />
      ) : shown.length === 0 ? (
        <Panel>
          <EmptyState
            art="⇄"
            title={
              tab === 'incoming'
                ? 'No offers waiting'
                : tab === 'outgoing'
                  ? 'You have no offers out'
                  : 'No past trades'
            }
          >
            {tab === 'history'
              ? 'Completed, declined and expired offers appear here.'
              : 'Visit a trainer and offer them something from your collection.'}
          </EmptyState>
        </Panel>
      ) : (
        shown.map((trade) => (
          <TradeCard key={trade.id} trade={trade} busy={busy === trade.id} onAct={act} />
        ))
      )}

      {composing !== null && (
        <ComposeTrade
          initialUsername={composing}
          onClose={() => setComposing(null)}
          onSent={() => {
            setComposing(null);
            setTab('outgoing');
            state.reload();
          }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   One offer
   ------------------------------------------------------------------------- */

const STATUS_STYLE: Record<string, { label: string; background: string; color: string }> = {
  PENDING: { label: 'Pending', background: 'var(--gold)', color: '#3a2a06' },
  ACCEPTED: { label: 'Completed', background: 'var(--leaf)', color: '#f2fff4' },
  DECLINED: { label: 'Declined', background: 'var(--berry)', color: '#fff6ef' },
  CANCELLED: { label: 'Withdrawn', background: 'var(--ink-faint)', color: '#fff' },
  EXPIRED: { label: 'Expired', background: 'var(--ink-faint)', color: '#fff' },
};

function TradeCard({
  trade,
  busy,
  onAct,
}: {
  trade: Trade;
  busy: boolean;
  onAct: (trade: Trade, action: 'accept' | 'decline' | 'cancel') => void;
}) {
  const status = STATUS_STYLE[trade.status] ?? STATUS_STYLE.PENDING!;

  return (
    <Panel>
      <div className="stack">
        <div className="row row--between row--wrap">
          <div className="row" style={{ gap: 8 }}>
            <span className="chip" style={{ background: status.background, color: status.color }}>
              {status.label}
            </span>
            <span className="tiny">
              {trade.iAmInitiator ? 'To' : 'From'}{' '}
              <Link to={`/grove/${trade.partner.username}`}>{trade.partner.username}</Link>
            </span>
          </div>
          <span className="tiny muted">
            {trade.status === 'PENDING' ? expiresIn(trade.expiresAt) : timeAgo(trade.respondedAt ?? trade.createdAt)}
          </span>
        </div>

        {trade.message && (
          <div className="pxwell tiny" style={{ padding: 'var(--gap-sm)', fontStyle: 'italic' }}>
            "{trade.message}"
          </div>
        )}

        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--gap-sm)' }}
        >
          <OfferSide title="You give" side={trade.youGive} tone="give" />
          <OfferSide title="You get" side={trade.youGet} tone="get" />
        </div>

        {(trade.canAccept || trade.canDecline || trade.canCancel) && (
          <div className="row row--wrap">
            {trade.canAccept && (
              <Button variant="go" loading={busy} onClick={() => onAct(trade, 'accept')}>
                Accept trade
              </Button>
            )}
            {trade.canDecline && (
              <Button disabled={busy} onClick={() => onAct(trade, 'decline')}>
                Decline
              </Button>
            )}
            {trade.canCancel && (
              <Button disabled={busy} onClick={() => onAct(trade, 'cancel')}>
                Withdraw offer
              </Button>
            )}
            {trade.canAccept && (
              <span className="tiny muted" style={{ alignSelf: 'center' }}>
                Check both sides carefully — a completed trade can't be undone.
              </span>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

function OfferSide({ title, side, tone }: { title: string; side: TradeSideDto; tone: 'give' | 'get' }) {
  const empty = side.pokemon.length === 0 && side.items.length === 0 && side.coins === 0;

  return (
    <div
      className="pxwell"
      style={{
        padding: 'var(--gap-sm)',
        borderTop: `3px solid ${tone === 'get' ? 'var(--leaf)' : 'var(--berry)'}`,
      }}
    >
      <div className="stack-sm">
        <div className="field__label">{title}</div>

        {empty ? (
          <div className="tiny muted">Nothing</div>
        ) : (
          <>
            {side.pokemon.length > 0 && (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 4 }}>
                {side.pokemon.map((pokemon) => (
                  <Link
                    key={pokemon.id}
                    to={`/pokemon/${pokemon.id}`}
                    className={`pxframe tray-item ${pokemon.isShiny ? 'pkmn-card--shiny' : ''}`}
                    style={{ color: 'inherit' }}
                  >
                    <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
                    <span className="tray-item__name">{pokemon.displayName}</span>
                    <span className="tiny muted">Lv {pokemon.level}</span>
                  </Link>
                ))}
              </div>
            )}

            {side.items.map((item) => (
              <div key={item.itemKey} className="row row--between tiny">
                <span>{item.name}</span>
                <span className="mono">×{item.quantity}</span>
              </div>
            ))}

            {side.coins > 0 && (
              <div className="row" style={{ gap: 6 }}>
                <span className="coin__dot coin__dot--gold" aria-hidden />
                <span className="mono tiny">{formatNumber(side.coins)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Composer
   ------------------------------------------------------------------------- */

function ComposeTrade({
  initialUsername,
  onClose,
  onSent,
}: {
  initialUsername: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const { push } = useToast();
  const [username, setUsername] = useState(initialUsername);
  const [message, setMessage] = useState('');
  const [givePokemon, setGivePokemon] = useState<Pokemon[]>([]);
  const [giveCoins, setGiveCoins] = useState(0);
  const [wantCoins, setWantCoins] = useState(0);
  const [wantNote, setWantNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const offerable = useAsync<OfferableResponse>(
    () => api.get('/trades/offerable', { search: debounced || undefined }),
    [debounced],
  );

  function toggle(pokemon: Pokemon) {
    setGivePokemon((current) =>
      current.some((entry) => entry.id === pokemon.id)
        ? current.filter((entry) => entry.id !== pokemon.id)
        : current.length >= 6
          ? current
          : [...current, pokemon],
    );
  }

  async function send() {
    setBusy(true);
    try {
      // "What I want" is expressed as coins plus a note. Asking for specific
      // Pokemon would mean browsing someone else's collection, which needs its
      // own screen — the note keeps the first version honest and simple.
      const fullMessage = [message.trim(), wantNote.trim() && `Looking for: ${wantNote.trim()}`]
        .filter(Boolean)
        .join(' — ');

      await api.post('/trades', {
        username,
        give: { pokemonIds: givePokemon.map((p) => p.id), items: [], coins: giveCoins },
        want: { pokemonIds: [], items: [], coins: wantCoins },
        message: fullMessage || null,
      });
      push('success', `Offer sent to ${username}.`, '⇄');
      onSent();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not send that offer.');
    } finally {
      setBusy(false);
    }
  }

  const maxCoins = offerable.data?.pokeCoins ?? 0;
  const canSend = username.trim().length > 0 && (givePokemon.length > 0 || giveCoins > 0 || wantCoins > 0);

  return (
    <Modal onClose={onClose} labelledBy="compose-title" wide>
      <div className="stack">
        <h2 id="compose-title">New trade offer</h2>

        <div className="field">
          <label className="field__label" htmlFor="trade-to">Trainer</label>
          <input
            id="trade-to"
            className="input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Their username"
            maxLength={20}
          />
        </div>

        <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
          <div className="stack-sm">
            <div className="row row--between">
              <span className="field__label">You give</span>
              <span className="tiny muted">{givePokemon.length} / 6 Pokémon</span>
            </div>

            {givePokemon.length > 0 && (
              <div className="row row--wrap" style={{ gap: 4 }}>
                {givePokemon.map((pokemon) => (
                  <button
                    key={pokemon.id}
                    type="button"
                    className="chip"
                    onClick={() => toggle(pokemon)}
                    title="Remove"
                    style={{ cursor: 'pointer' }}
                  >
                    {pokemon.displayName} ✕
                  </button>
                ))}
              </div>
            )}

            <div className="row">
              <span className="coin__dot coin__dot--gold" aria-hidden />
              <input
                className="input"
                type="number"
                min={0}
                max={maxCoins}
                value={giveCoins}
                onChange={(event) =>
                  setGiveCoins(Math.max(0, Math.min(maxCoins, Number(event.target.value) || 0)))
                }
                style={{ maxWidth: 160 }}
              />
              <span className="tiny muted">of {formatNumber(maxCoins)}</span>
            </div>
          </div>
        </div>

        <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
          <div className="stack-sm">
            <span className="field__label">You want</span>
            <div className="row">
              <span className="coin__dot coin__dot--gold" aria-hidden />
              <input
                className="input"
                type="number"
                min={0}
                value={wantCoins}
                onChange={(event) => setWantCoins(Math.max(0, Number(event.target.value) || 0))}
                style={{ maxWidth: 160 }}
              />
              <span className="tiny muted">PokéCoins from them</span>
            </div>
            <input
              className="input"
              value={wantNote}
              onChange={(event) => setWantNote(event.target.value)}
              placeholder="Anything else you're after (e.g. a shiny Oddish)"
              maxLength={100}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="trade-msg">Message <span className="muted">(optional)</span></label>
          <input
            id="trade-msg"
            className="input"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={150}
            placeholder="Say hello"
          />
        </div>

        <div>
          <div className="row row--between" style={{ marginBottom: 6 }}>
            <span className="field__label">Choose Pokémon to give</span>
            <input
              className="input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              style={{ maxWidth: 200 }}
            />
          </div>

          {offerable.data?.lockedNote && (
            <p className="tiny muted" style={{ marginTop: 0 }}>{offerable.data.lockedNote}</p>
          )}

          {offerable.loading ? (
            <div className="pxwell skeleton" style={{ height: 160 }} />
          ) : (offerable.data?.pokemon.length ?? 0) === 0 ? (
            <EmptyState art="◌" title="Nothing available to trade">
              Locked Pokémon and any already promised in another offer are hidden.
            </EmptyState>
          ) : (
            <div className="grove-tray">
              {offerable.data!.pokemon.map((pokemon) => {
                const picked = givePokemon.some((entry) => entry.id === pokemon.id);
                return (
                  <button
                    key={pokemon.id}
                    type="button"
                    className={`pxframe tray-item ${pokemon.isShiny ? 'pkmn-card--shiny' : ''}`}
                    onClick={() => toggle(pokemon)}
                    style={{ outline: picked ? '3px solid var(--leaf)' : 'none', outlineOffset: 2 }}
                  >
                    <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
                    <span className="tray-item__name">{pokemon.displayName}</span>
                    <span className="tiny muted">Lv {pokemon.level}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="row">
          <Button onClick={onClose}>Cancel</Button>
          <div className="spacer" />
          <Button variant="go" disabled={!canSend} loading={busy} onClick={() => void send()}>
            Send offer
          </Button>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          Once sent, an offer can't be edited — only withdrawn and re-sent. It expires in 72 hours.
        </p>
      </div>
    </Modal>
  );
}
